// src/lib/managers/conversation/question.ts

/**
 * Une question à choix posée à l'utilisateur à la fin d'un tour du contrat par étapes
 * (Api-Ai : docs/refonte-assistant/CONCEPTION-questions-a-choix.md).
 *
 * La question est une donnée : son texte et quelques choix. L'app l'affiche en carte ; cliquer un
 * choix RÉPOND à la question, rien de plus : sa `reponse` part comme le message suivant de
 * l'utilisateur. Rien ne reste bloqué s'il écrit autre chose.
 *
 * Étape 1 : l'app pose elle-même la question quand c'est elle qui détient l'ambiguïté, sans modèle :
 * deux contacts pour un nom, ou un nom introuvable qui ressemble à un contact (`contactQuestion`).
 */

import type { ContactCandidate } from "../contact";
import { clip, oneLine } from "./contractText";

/** Un choix d'une question, tel qu'il voyage dans le contrat. */
export type EtapesChoix = {
    libelle: string;
    /** Une ligne d'explication, en plus petit (une adresse). */
    detail?: string;
    /** Le message envoyé quand l'utilisateur clique ; absent : le libellé. */
    reponse?: string;
};

/** Une question à choix, telle qu'elle voyage dans le contrat (résultat d'une étape, fin du tour). */
export type EtapesQuestion = { question: string; choix: EtapesChoix[] };

/** Bornes du contrat (les mêmes que l'Api-Ai, `assistant/etapes/question.py`). */
export const QUESTION_CHOICES_MAX = 4;
const QUESTION_MAX = 200;
const LIBELLE_MAX = 80;
const DETAIL_MAX = 120;
const REPONSE_MAX = 300;
/** Un nom demandé, cité dans la question et dans la réponse, au plus. */
const NAME_MAX = 60;

/**
 * La question d'une fin de tour (ou d'un résultat d'étape), lue sans confiance : son texte, de 1 à 4
 * choix tous différents, chacun avec un libellé. Illisible : aucune, jamais une carte à moitié juste.
 */
export function readQuestion(raw: unknown): EtapesQuestion | undefined {
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
        return undefined;
    }
    const { question, choix } = raw as Record<string, unknown>;
    const text = oneLine(question, QUESTION_MAX);
    if (
        !text ||
        !Array.isArray(choix) ||
        choix.length < 1 ||
        choix.length > QUESTION_CHOICES_MAX
    ) {
        return undefined;
    }
    const lus: EtapesChoix[] = [];
    for (const item of choix) {
        if (typeof item !== "object" || item === null) return undefined;
        const { libelle, detail, reponse } = item as Record<string, unknown>;
        const label = oneLine(libelle, LIBELLE_MAX);
        const line = oneLine(detail, DETAIL_MAX);
        const reply = oneLine(reponse, REPONSE_MAX);
        if (
            !label ||
            (detail !== undefined && detail !== null && !line) ||
            (reponse !== undefined && reponse !== null && !reply)
        ) {
            return undefined;
        }
        lus.push({
            libelle: label,
            ...(line ? { detail: line } : {}),
            ...(reply ? { reponse: reply } : {}),
        });
    }
    const keys = new Set(
        lus.map((c) => `${c.libelle}\n${c.detail ?? ""}`.toLowerCase()),
    );
    return keys.size === lus.length
        ? { question: text, choix: lus }
        : undefined;
}

/** « Nom <adresse> », ou l'adresse seule. */
const contactLabel = (contact: ContactCandidate): string =>
    contact.displayName && contact.displayName !== contact.email
        ? `${clip(contact.displayName, LIBELLE_MAX)} <${contact.email}>`
        : contact.email;

/** Ce que `ContactManager.resolveBestContact` a fait d'un nom. */
type ContactResolution = {
    emails: string[];
    candidates?: ContactCandidate[];
    ambiguous?: boolean;
    suggestions?: ContactCandidate[];
};

/** Une question sur un contact, et l'adresse que désigne chacune de ses réponses. */
export type ContactQuestion = {
    question: EtapesQuestion;
    /** Le nom demandé, en minuscules. */
    name: string;
    /** Le même, tel qu'il a été demandé : la clé du fait retenu quand l'utilisateur choisit. */
    label: string;
    /** Réponse envoyée au clic → adresse choisie. */
    emailsByReply: Map<string, string>;
};

const isAmbiguous = (resolution: ContactResolution): boolean =>
    Boolean(resolution.ambiguous) && (resolution.candidates ?? []).length > 1;

/**
 * Les contacts parmi lesquels l'utilisateur aurait à choisir pour ce nom : les candidats d'un nom
 * ambigu, ou les contacts qui ressemblent à un nom introuvable. Aucun : rien à demander.
 */
function proposedContacts(resolution: ContactResolution): ContactCandidate[] {
    if (isAmbiguous(resolution)) return resolution.candidates ?? [];
    return resolution.emails.length === 0 ? (resolution.suggestions ?? []) : [];
}

/**
 * Une personne identifiée l'est pour toute la conversation, quel que soit le nom employé
 * (CONCEPTION-personnes-de-la-conversation de l'Api-Ai, R1). Parmi les contacts que l'app proposerait
 * de choisir pour ce nom, exactement une adresse déjà choisie dans la conversation (`chosen`, en
 * minuscules) : c'est elle, sans question. Aucune, ou plusieurs : la résolution telle quelle.
 */
export function withChosenContact<R extends ContactResolution>(
    resolution: R,
    chosen: ReadonlySet<string>,
): R {
    const known = proposedContacts(resolution).filter((contact) =>
        chosen.has(contact.email.toLowerCase()),
    );
    if (known.length !== 1) return resolution;
    return { ...resolution, emails: [known[0].email], ambiguous: false };
}

/**
 * La question que l'app pose sur un nom de contact, ou `undefined` s'il n'y a rien à demander :
 *  - plusieurs contacts presque aussi pertinents (`ambiguous`) : « Lequel ? », les candidats ;
 *  - nom introuvable, mais des contacts lui ressemblent (`suggestions`) : « Vouliez-vous dire : ».
 * Règle déterministe, sans modèle. Chaque réponse nomme le contact et son adresse : au tour suivant,
 * l'assistant lit une adresse écrite par l'utilisateur, qu'il garde telle quelle.
 */
export function contactQuestion(
    requested: string,
    resolution: ContactResolution,
): ContactQuestion | undefined {
    const ambiguous = isAmbiguous(resolution);
    const proposed = proposedContacts(resolution);
    const name = clip(requested, NAME_MAX);
    const choices = proposed
        .filter((contact) => contact.email.length <= DETAIL_MAX)
        .slice(0, QUESTION_CHOICES_MAX);
    if (choices.length === 0 || !name) return undefined;

    const emailsByReply = new Map<string, string>();
    const choix = choices.map((contact): EtapesChoix => {
        const reponse = `Pour « ${name} » : ${contactLabel(contact)}`;
        emailsByReply.set(reponse, contact.email);
        const named =
            contact.displayName && contact.displayName !== contact.email;
        return {
            libelle: named
                ? clip(contact.displayName, LIBELLE_MAX)
                : clip(contact.email, LIBELLE_MAX),
            ...(named ? { detail: contact.email } : {}),
            reponse,
        };
    });
    return {
        question: {
            question: ambiguous
                ? `Plusieurs contacts correspondent à « ${name} ». Lequel ?`
                : `Je ne trouve pas « ${name} » dans vos contacts. Vouliez-vous dire :`,
            choix,
        },
        name: requested.trim().toLowerCase(),
        label: name,
        emailsByReply,
    };
}
